import { useField, useFormikContext } from 'formik';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  SelectProps,
  SelectChangeEvent,
  Typography,
  Badge,
  FormControlProps,
  FormHelperTextProps,
  ClickAwayListener,
  ListItem,
  Input,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { BootstrapSelect } from './SelectField';
import { useEffect, useRef, useState } from 'react';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import {
  RemoveAsterikFromstring,
  checkAsterikInString,
  getWCAGAutoCompleteValue,
} from '../../utils/utilityFunctions';

export interface IKeyValuePair {
  key: any;
  value: string;
}

interface IBaseProps {
  label?: string;
  searchFieldPlaceholder?: string;
  removeSelectionText?: string;
  helperText?: string;
  formControlProps?: FormControlProps;
  formHelperTextProps?: FormHelperTextProps;
  maxVisibleOptions?: number;
  showAll?: boolean;
  noRemoveSelectionOption?: boolean;
}

interface IDefaultKeyValuePair extends IBaseProps {
  options: IKeyValuePair[];
}

interface ICustomKeyValuePair extends IBaseProps {
  keyPropFn: (option: IKeyValuePair | any) => any;
  valuePropFn: (option: IKeyValuePair | any) => string | number;
  options: any[];
}

export type SearchableSelectProps = (IDefaultKeyValuePair | ICustomKeyValuePair) & SelectProps;

interface IClickAwayListenerWrapperProps {
  searchFieldPlaceholder: string | undefined;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}
export function renderFilteredOptions(
  items: { key: string | number; value: string | number }[],
  query: string,
  fieldValue: string | number[]
): any {
  const filteredOptions =
    items &&
    items.filter &&
    items.filter((item: IKeyValuePair | any) => {
      return (
        !item.value ||
        (item.value && item.value.toString().toLowerCase().indexOf(query.toLowerCase()) !== -1)
      );
    });

  // FIX: Explicitly check for an empty array *only when a query exists* to prevent empty popup bugs
  const showNoResults = query && (!filteredOptions || filteredOptions.length === 0);

  // FIX: Return a persistent live region parent wrapper container block.
  // Screen readers like NVDA need an active live element container present early in the DOM layout
  // to announce dynamic string switches cleanly without dropping the speech queue.
  return (
    <>
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          border: 0,
        }}>
        {showNoResults ? 'No Results found' : ''}
      </div>

      {filteredOptions?.length > 0
        ? filteredOptions.map((option: { key: string | number; value: string | number }) => {
            const isChecked = fieldValue?.indexOf(option.key as never) > -1;
            const optionId = `opt-${option.key}`; // FIX: Added unique semantic ID mappings

            return (
              <MenuItem
                key={option.key}
                value={option.key}
                id={optionId}
                role="option" // FIX: Standardizes option roles to prevent "blank" reading loops
                aria-labelledby={optionId}
                sx={{ py: { xs: 1, sm: 0 } }}
                aria-selected={isChecked}>
                <Checkbox
                  checked={isChecked}
                  tabIndex={-1}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'common.black',
                    '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: 'unset' },
                  }}
                />
                <ListItemText
                  id={optionId} // FIX: Links labels back to the parent item option
                  primary={option.value}
                  sx={{
                    '& .MuiTypography-root': {
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'common.black',
                      whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' },
                    },
                  }}
                />
              </MenuItem>
            );
          })
        : showNoResults && (
            <Typography
              component="h2"
              sx={{
                paddingTop: '6px',
                paddingBottom: '6px',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
                paddingLeft: '16px',
                paddingRight: '16px',
                fontSize: '14px',
                fontWeight: 500,
              }}>
              No Results found
            </Typography>
          )}
    </>
  );
}

export function SearchFieldWrapper({
  searchFieldPlaceholder,
  setQuery,
}: IClickAwayListenerWrapperProps & {
  searchFieldPlaceholder: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element {
  const searchInputRef = useRef<HTMLInputElement>(null);

  // **Auto-focus hook**: Shifts focus into the input box as soon as the menu popover opens
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // **Reverse navigation tracking hook**: Connects a listener to the parent popover box
  useEffect(() => {
    const inputEl = searchInputRef.current;
    if (!inputEl) return;

    // Locate the closest wrapper container tree element holding your option items
    const menuPaper = inputEl.closest('.MuiPaper-root');
    if (!menuPaper) return;

    const handleMenuKeyDown = (e: KeyboardEvent) => {
      // Monitor interactions if focus currently resides inside the selection choices
      if (document.activeElement && document.activeElement.getAttribute('role') === 'option') {
        const activeOption = document.activeElement as HTMLElement;

        // Trace and extract all visible non-disabled option elements in the menu rows
        const allOptions = Array.from(
          menuPaper.querySelectorAll('li[role="option"]:not([disabled])')
        ) as HTMLElement[];

        // Check if the user is currently on the very first interactive option row item
        if (e.key === 'ArrowUp' && activeOption === allOptions[0]) {
          e.preventDefault();
          e.stopPropagation();
          inputEl.focus(); // **Seamless reverse navigation**: Snaps focus back into search box
        }
      }
    };

    menuPaper.addEventListener('keydown', handleMenuKeyDown as EventListener, true);
    return () => {
      menuPaper.removeEventListener('keydown', handleMenuKeyDown as EventListener, true);
    };
  }, []);

  return (
    <ClickAwayListener onClickAway={() => null}>
      <ListItem sx={{ p: 0 }} role="none">
        <Input
          id="select-search-input"
          inputRef={searchInputRef}
          placeholder={searchFieldPlaceholder || 'Search...'}
          autoComplete="off"
          fullWidth
          inputProps={{ 'aria-label': 'Search options' }}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => {
            // **Fix typing conflicts**: Stop any character key events from bubbling up to MUI
            // This prevents letters like 'W' or 'S' from being tracked as menu item shortcuts
            if (e.key.length === 1) {
              e.stopPropagation();
              return;
            }

            if (e.key === ' ') {
              e.stopPropagation();
            } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
              // **Forward loop routing**: Jumps keyboard focus cleanly down into option row 1
              e.preventDefault();
              e.stopPropagation();

              const menuPaper = searchInputRef.current?.closest('.MuiPaper-root');
              if (menuPaper) {
                const firstOption = menuPaper.querySelector(
                  'li[role="option"]:not([disabled])'
                ) as HTMLElement;
                if (firstOption) {
                  firstOption.focus();
                }
              }
            } else if (e.key === 'ArrowUp') {
              e.stopPropagation(); // Stops dropdown container from closing prematurely on accident
            }
          }}
          startAdornment={
            <InputAdornment position="start">
              <IconButton tabIndex={-1} type="button" aria-label="Search">
                <SearchSharpIcon sx={{ color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          }
          sx={{ mx: 2, my: 1 }}
        />
      </ListItem>
    </ClickAwayListener>
  );
}

export const MultipleSelect = ({
  name,
  label,
  items,
  renderText = '',
  disabled = false,
  placeholder,
  ...rest
}: SelectProps & {
  name: string;
  label: string;
  items: { key: string | number; value: string | number }[];
  renderText?: string;
  disabled?: boolean;
}): JSX.Element => {
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : '';
  const { setFieldValue } = useFormikContext();
  const [query, setQuery] = useState('');

  const labelId = `${name}-label`;
  const selectId = `${name}-select`;
  const errorId = `${name}-error`;

  const handleChange = (e: SelectChangeEvent<unknown>): void => {
    const { target: fieldTarget } = e;
    const { value } = fieldTarget;
    if ((value as string[] | number[])[(value as string[] | number[])?.length - 1] === 'all') {
      setFieldValue(
        name,
        field.value?.length === items.map((item) => item.key).length
          ? []
          : items.map((item) => item.key)
      );
      return;
    }
    setFieldValue(name, value);
  };

  const isAllChecked = items.length > 0 && field.value?.length === items.length;
  const autoCompleteToken = getWCAGAutoCompleteValue(name || '');

  /* FIX: Corrected variable name from 'label' to 'formLabel' to resolve the TS compile error */
  const containsAsterisk = label ? label.includes('*') : false;
  const { sx: restSx, ...cleanRest } = rest;

  return (
    <FormControl fullWidth sx={{ my: { xs: 1, sm: 1, md: 2 } }}>
      <InputLabel
        shrink
        id={labelId}
        htmlFor={selectId}
        sx={{ transform: 'unset', fontSize: 14, fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}>
        {RemoveAsterikFromstring(label)}
        {checkAsterikInString(label) && <span style={{ color: 'red' }}>*</span>}
      </InputLabel>
      <BootstrapSelect
        id={selectId}
        labelId={labelId}
        aria-invalid={!!errorText}
        inputProps={{
          id: selectId,
          ...(autoCompleteToken ? { autoComplete: autoCompleteToken } : {}),
          ...(containsAsterisk && {
            'aria-label': `${label?.replace('*', '').trim()} required`,
            'aria-required': true,
          }),
        }}
        sx={{
          mt: '32px',
          ...restSx,
          '& .Mui-disabled': {
            cursor: !disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },

          '&.Mui-focusVisible .MuiSelect-select': {
            border: '2px solid #2057A6',
            borderRadius: '4px',
            padding: '9px 11px',
          },
        }}
        MenuProps={{
          sx: {
            '.MuiPaper-root ': {
              maxWidth: { xs: '100%', sm: '31%', md: '26%' },
              maxHeight: '20%',
              overflowX: 'auto',
            },
          },
          disableAutoFocusItem: false,
          MenuListProps: {
            disableListWrap: true,
          },
        }}
        onClose={() => {
          setQuery('');
        }}
        multiple
        {...field}
        {...cleanRest}
        renderValue={(selected) => {
          const selectedKeys = [];
          for (let i = 0; i < (selected as string[]).length; i++) {
            const key = (selected as unknown as string[])[i];
            const value = items.find(
              (_ele: { key: string | number; value: string | number }) => _ele.key === key
            )?.value;
            if (value) {
              selectedKeys.push(value);
            }
          }
          return renderText ? (
            <Typography component={'span'}>
              {renderText}
              <Badge badgeContent={selectedKeys.length} color="info" sx={{ pl: 3 }}></Badge>
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedKeys.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  sx={{ fontSize: 14, fontWeight: 500, color: 'common.black' }}
                />
              ))}
            </Box>
          );
        }}
        value={field.value || []}
        disabled={disabled}
        onChange={handleChange}>
        {items?.length > 0 && (
          <SearchFieldWrapper searchFieldPlaceholder={'Search...'} setQuery={setQuery} />
        )}
        {items?.length > 0 && !query && (
          <MenuItem value="all" sx={{ py: 0 }} aria-selected={isAllChecked}>
            <Checkbox
              checked={isAllChecked}
              tabIndex={-1}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: 'common.black',
                '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: 'unset' },
              }}
            />
            <ListItemText
              primary="Select All"
              sx={{ '& .MuiTypography-root': { fontSize: 14, fontWeight: 500 } }}
            />
          </MenuItem>
        )}
        {renderFilteredOptions(items, query, field.value || [])}
      </BootstrapSelect>
      {errorText && (
        <FormHelperText
          error
          id={errorId}
          role="alert"
          aria-live="assertive"
          sx={{ marginLeft: 'unset' }}>
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
